import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Form, Input, Select, Button, message, Modal, theme } from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import { getProjectGroupList, createProject } from '../../../services/project';
import type { ProjectGroup, CreateProjectParams } from '../../../types/project';

const DUPLICATE_PROJECT_NAME_MODAL_TEXT =
  '已存在相同项目名称(已忽略首尾空格并将字母统一为大写）,请返回修改或将项目名称留空';

function isDuplicateProjectNameError(nameField: unknown): boolean {
  if (nameField == null) return false;
  const text = Array.isArray(nameField) ? nameField.join(' ') : String(nameField);
  return text.includes('相同项目名称') || text.includes('该主账号下已存在');
}

const { Title } = Typography;

const labelWithHint = (title: string, hint: string) => (
  <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 8, rowGap: 4 }}>
    <span>{title}</span>
    <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
      {hint}
    </Typography.Text>
  </span>
);
const { TextArea } = Input;
const { Option } = Select;

/** value 与后端 ProjectType 一致；title 为选项名，description 为说明（下拉内单独样式展示） */
const PROJECT_TYPES: { value: string; title: string; description: string }[] = [
  {
    value: 'OrderFulfillment',
    title: '一件代发',
    description: '批量或零散入库,小包裹单件或者多件出库',
  },
  {
    value: 'StockTransfer',
    title: '备货中转',
    description: '批量整箱/整托盘入库,入库不拆箱,整箱/整托盘出库',
  },
  {
    value: 'Relabeling',
    title: '平台退仓简单处理:贴换标/清点',
    description: '来自于亚马逊或其他平台移仓,清点/换标后重发至FBA等平台仓库',
  },
  {
    value: 'ReturnProcessing',
    title: '买家退货/平台退仓复杂处理:检测-翻新-取出或安装配件-销毁等',
    description: '来自于平台移仓或买家直接退货,需要检测/翻新等处理后重新销售或销毁',
  },
  {
    value: 'Mixed',
    title: '多种业务模式混合',
    description: '以上多种业务均可能涉及',
  },
  {
    value: 'Other',
    title: '其他',
    description: '无法归类的其他业务类型,建议先联系本司确认是否属于可承接的业务类型',
  },
];

const CreateProject: React.FC = () => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // 获取项目组列表
  const fetchProjectGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await getProjectGroupList({ page: 1, page_size: 100 });
      setProjectGroups(response.data.results);
    } catch (error: any) {
      console.error('获取项目组列表失败:', error);
      message.error(error.response?.data?.detail || '获取项目组列表失败');
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    fetchProjectGroups();
  }, []);

  // 检查用户权限（允许：客户、管理员；禁止：员工）
  useEffect(() => {
    if (user && !(user.role === 'customer' || user.role === 'admin')) {
      message.error('只有客户或管理员可以创建项目');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      // 构造提交数据
      const nameTrimmed = (values.name ?? '').trim();
      const submitData: CreateProjectParams = {
        project_type: values.project_type,
        description: values.description || undefined,
        project_group: values.project_group ? parseInt(values.project_group, 10) : undefined,
      };
      if (nameTrimmed) {
        submitData.name = nameTrimmed;
      } else {
        submitData.name = null;
      }
      
      const response = await createProject(submitData);
      
      message.success('项目创建成功');
      if (user?.role === 'customer') {
        navigate('/dashboard/customer/projects/list');
      } else {
        navigate('/dashboard/projects/list');
      }
    } catch (error: any) {
      console.error('创建项目失败:', error);
      const data = error.response?.data;
      if (data && isDuplicateProjectNameError(data.name)) {
        Modal.warning({
          title: '提示',
          content: DUPLICATE_PROJECT_NAME_MODAL_TEXT,
        });
      } else if (data?.detail) {
        message.error(data.detail);
      } else if (data?.non_field_errors) {
        message.error(data.non_field_errors.join(', '));
      } else if (data) {
        const fieldErrors = Object.entries(data)
          .filter(([key]) => key !== 'detail' && key !== 'non_field_errors')
          .map(([key, value]) => `${key}: ${value}`);
        if (fieldErrors.length > 0) {
          message.error(fieldErrors.join('\n'));
        } else {
          message.error('创建项目失败');
        }
      } else {
        message.error(error.message || '创建项目失败');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user || !(user.role === 'customer' || user.role === 'admin')) {
    return null;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>创建新项目</Title>
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            project_type: 'Other',
          }}
        >
          <Form.Item
            name="project_type"
            label={labelWithHint(
              '项目类型',
              '请选择项目主要业务类型,仅用于提示作用,不会影响后续项目管理',
            )}
            rules={[{ required: true, message: '请选择项目类型' }]}
          >
            <Select
              placeholder="请选择项目类型"
              popupMatchSelectWidth={false}
              dropdownStyle={{ maxWidth: 720 }}
              options={PROJECT_TYPES.map(t => ({
                value: t.value,
                label: t.title,
                description: t.description,
              }))}
              optionRender={(oriOption) => {
                const d = oriOption.data as {
                  label: string;
                  description?: string;
                  value: string;
                };
                return (
                  <div style={{ padding: '6px 0', maxWidth: 680 }}>
                    <div style={{ fontWeight: 600, color: token.colorText, lineHeight: 1.4 }}>
                      {d.label}
                    </div>
                    {d.description ? (
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 12,
                          lineHeight: 1.55,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {d.description}
                      </div>
                    ) : null}
                  </div>
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label={labelWithHint(
              '自定义项目名称',
              '请输入项目名称,便于后续项目管理,名称不可重复; 可留空',
            )}
          >
            <Input placeholder="请输入项目名称,可留空" maxLength={100} allowClear />
          </Form.Item>

          <Form.Item
            name="description"
            label="项目备注"
          >
            <TextArea
              placeholder="请输入项目备注信息,可留空"
              autoSize={{ minRows: 4, maxRows: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="project_group"
            label={labelWithHint(
              '所属项目组',
              '请选择所属项目组,便于后续集中管理;可留空',
            )}
          >
            <Select
              placeholder="请选择所属项目组"
              loading={loadingGroups}
              allowClear
            >
              {projectGroups.map(group => (
                <Option key={group.id} value={group.id}>
                  {group.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              创建项目
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => navigate('/dashboard/projects/list')}
            >
              取消
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateProject; 